import { z } from 'zod';
import assertUnreachable from '../../utils/assertUnreachable';
import { toLlmJsonSchema } from '../../utils/zodJsonSchema';
/**
 * Ordered pipeline stages. Index position determines progression — used to
 * skip completed stages when a build is retried after a worker crash.
 * 'ready' is included as the final stage; 'error' is terminal but not a
 * stage (not reachable through normal progression). 'compact' is optional —
 * most builds go straight from 'catalog' to 'generating'.
 */
export const APP_VERSION_STAGE_ORDER = [
    'pending',
    'sandbox',
    'catalog',
    'compact',
    'generating',
    'building',
    'packaging',
    'ready',
];
export const APP_VERSION_TERMINAL_STATUSES = ['ready', 'error'];
/**
 * Error message stamped on a version when the user cancels its build. There is
 * no dedicated 'cancelled' status — cancellation is an 'error' carrying this
 * marker, which the pipeline also reads to detect that a previous prompt was
 * cancelled mid-generation.
 */
export const APP_VERSION_CANCELLED_BY_USER = 'Cancelled by user';
export const isAppVersionInProgress = (status) => !APP_VERSION_TERMINAL_STATUSES.includes(status);
/**
 * Data apps are created with an empty name and only get an auto-generated
 * title after their first version builds successfully. If that build never
 * completes the name stays blank. Everywhere an app name is shown to the user,
 * fall back to a stable, identifiable placeholder instead of rendering nothing
 * — the uuid suffix keeps two unnamed apps distinguishable. Use this as the
 * single source of truth so the convention stays consistent across the UI.
 */
export const getAppDisplayName = (name, appUuid) => name.trim().length > 0 ? name : `Untitled app ${appUuid.slice(0, 8)}`;
/**
 * Attachment cap per generation/iteration request. Shared by the frontend
 * composer and the backend validator so the limits can't drift apart.
 */
export const MAX_APP_FILES_PER_VERSION = 10;
/** Starter template for a single-tile renderer that emits a typed viz schema. */
export const DATA_APP_VIZ_TEMPLATE = 'data_app_viz';
export const DATA_APP_TEMPLATES = [
    'dashboard',
    'slideshow',
    'pdf',
    'custom',
    DATA_APP_VIZ_TEMPLATE,
];
export const DATA_APP_CREATION_EXPERIENCES = [
    'app_builder',
    'explorer_chart_config',
    'chart_type_builder',
    'ai_agent',
    'mcp',
];
/** Coding agent used inside the data-app sandbox. */
export const DATA_APP_CODING_AGENTS = ['claude', 'codex'];
/**
 * Claude model used to generate / iterate the data app inside the sandbox.
 * Mapped to the Claude CLI's `--model <alias>` flag verbatim. Persisted
 * per-version on `AppVersionResources.claudeModel` so the user can switch
 * mid-iteration without losing the record of which model built which version.
 */
export const DATA_APP_CLAUDE_MODELS = ['opus', 'sonnet', 'haiku'];
export const DEFAULT_DATA_APP_CLAUDE_MODEL = 'sonnet';
/**
 * Codex model used to generate / iterate the data app inside the sandbox.
 * These are passed to `codex exec --model` verbatim. Kept separate from the
 * Claude model contract because Claude visibility is managed by org admins,
 * while Codex currently exposes this fixed instance-level set.
 */
export const DATA_APP_CODEX_MODELS = [
    'gpt-5.6-sol',
    'gpt-5.6-terra',
    'gpt-5.6-luna',
];
export const DEFAULT_DATA_APP_CODEX_MODEL = 'gpt-5.6-terra';
export const getVisibleDataAppClaudeModels = (visibility) => DATA_APP_CLAUDE_MODELS.filter((model) => visibility?.[model] !== false);
/**
 * Fallback preference when the default model is unavailable. Deliberately NOT
 * `DATA_APP_CLAUDE_MODELS` order: that list is ordered by capability for
 * display (Opus first), and reusing it here would promote a request to the
 * most expensive model when an admin hides Sonnet — turning a cost-control
 * action into a cost increase. Ordered by ascending cost instead.
 */
const DATA_APP_CLAUDE_MODEL_FALLBACK_ORDER = [
    'sonnet',
    'haiku',
    'opus',
];
/**
 * The model to use when the caller doesn't specify one. Prefers
 * `DEFAULT_DATA_APP_CLAUDE_MODEL`, then the cheapest remaining visible model,
 * so hiding the system default never leaves nothing selectable and never
 * silently upgrades the user to a pricier model. Returns `null` only when
 * every model has been hidden (rejected at write time — see
 * AiOrganizationSettingsService).
 */
export const resolveDefaultDataAppClaudeModel = (visibleModels) => {
    if (visibleModels.includes(DEFAULT_DATA_APP_CLAUDE_MODEL)) {
        return DEFAULT_DATA_APP_CLAUDE_MODEL;
    }
    return (DATA_APP_CLAUDE_MODEL_FALLBACK_ORDER.find((model) => visibleModels.includes(model)) ?? null);
};
export const resolveDefaultVisibleDataAppClaudeModel = (visibility) => resolveDefaultDataAppClaudeModel(getVisibleDataAppClaudeModels(visibility));
/**
 * Fold clarifications into the prompt sent to the sandbox. The persisted
 * prompt stays as the user's original words — clarifications travel
 * separately on `resources.clarifications` for chat rendering.
 */
export const formatPromptWithClarifications = (prompt, clarifications) => {
    if (!clarifications || clarifications.length === 0)
        return prompt;
    const qa = clarifications
        .map((c, i) => `${i + 1}. Q: ${c.question.trim()}\n   A: ${c.answer.trim()}`)
        .join('\n');
    return `${prompt}\n\nClarifications:\n${qa}`;
};
export const MAX_APP_VIZ_BUILD_SAMPLE_ROWS = 10;
export const MAX_APP_VIZ_BUILD_SAMPLE_FIELDS = 20;
export const MAX_APP_VIZ_BUILD_SAMPLE_CELL_CHARS = 500;
export const MAX_APP_VIZ_BUILD_ELEMENT_REFS = 5;
/** What started a data app thread. */
export const APP_THREAD_ORIGINS = ['builder', 'ai_thread', 'import'];
export const APP_UPGRADE_PROMPT_LABEL = 'Upgrade to the latest app template';
const uniqueNames = (arr) => new Set(arr.map((a) => a.name)).size === arr.length;
export const MAX_DATA_APP_VIZ_FIELD_DESCRIPTION_LENGTH = 160;
const optionalInputHelp = (maxLength) => {
    const text = z.string().trim();
    return (maxLength === undefined ? text : text.max(maxLength))
        .nullable()
        .transform((value) => value || undefined)
        .optional();
};
const nullableOptionalFieldExamples = () => z
    .array(z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .nullable()
    .transform((value) => value ?? undefined)
    .optional();
const vizInputGuidance = (strict) => optionalInputHelp(strict ? 200 : undefined).describe(strict
    ? 'Optional setup guidance in at most two short, plain sentences (maximum 200 characters). Describe the expected rows, ordering where relevant, and how to change a query that has a different shape.'
    : 'Optional setup guidance describing the expected rows, ordering, and how to adapt a query with a different shape.');
const optionBase = {
    name: z
        .string()
        .min(1)
        .describe('Key the component reads from `options`. Unique across options, no spaces.'),
    label: z
        .string()
        .describe('Human label shown next to the control in the config panel.'),
    group: z
        .string()
        .nullable()
        .transform((value) => value ?? undefined)
        .optional()
        .describe('Optional tab name. Options sharing a group are rendered in the same config tab; ungrouped options share a default tab.'),
};
const vizField = (strict) => z.object({
    name: z
        .string()
        .min(1)
        .describe('Key the component reads from `fieldMapping`. Unique across fields, no spaces.'),
    label: z
        .string()
        .describe('Human label shown in the field-mapping UI.'),
    type: z
        .enum(['dimension', 'metric', 'series', 'column'])
        .describe('dimension = a category/grouping column, metric = a numeric measure, series = a dimension used to split or colour the chart, column = any result column (metric or dimension) — use when the chart handles non-numeric values.'),
    required: z
        .boolean()
        .describe('false only when the chart still renders with this field unmapped.'),
    multiple: z
        .boolean()
        .nullable()
        .transform((value) => value ?? undefined)
        .optional()
        .describe('Whether this slot accepts an ordered collection of fields. Omit or set false for one field.'),
    description: optionalInputHelp(strict ? MAX_DATA_APP_VIZ_FIELD_DESCRIPTION_LENGTH : undefined).describe(strict
        ? 'Optional mapping help in one or two short, plain sentences (maximum 160 characters). Explain what this field represents and how to choose it, without naming a particular query.'
        : 'Optional reusable mapping help explaining what this field represents.'),
});
const vizFieldsDescription = 'Every data column the component reads. Declare exactly what you read — no more, no less.';
const vizFieldsForRead = z
    .array(vizField(false).extend({
    examples: nullableOptionalFieldExamples().describe('Optional legacy scalar display examples for this field. Each value stands alone.'),
}))
    .describe(vizFieldsDescription);
const vizFieldsForGeneration = z
    .array(vizField(true))
    .describe(vizFieldsDescription);
const vizConfigOptions = z.array(z.discriminatedUnion('type', [
    z.object({
        ...optionBase,
        type: z.literal('boolean'),
        default: z
            .boolean()
            .describe('Value used until the viewer changes it.'),
    }),
    z.object({
        ...optionBase,
        type: z.literal('select'),
        choices: z
            .array(z.object({
            value: z
                .string()
                .describe('Value delivered on `options`.'),
            label: z.string().describe('Human label in the list.'),
        }))
            .min(1)
            .describe('The selectable choices; at least one.'),
        default: z
            .string()
            .describe('One of the declared choice `value`s.'),
    }),
    z.object({
        ...optionBase,
        type: z.literal('number'),
        default: z
            .number()
            .describe('Value used until the viewer changes it.'),
        min: z
            .number()
            .nullable()
            .transform((value) => value ?? undefined)
            .optional()
            .describe('Optional lower bound.'),
        max: z
            .number()
            .nullable()
            .transform((value) => value ?? undefined)
            .optional()
            .describe('Optional upper bound.'),
    }),
    z.object({
        ...optionBase,
        type: z.literal('text'),
        default: z
            .string()
            .describe('Value used until the viewer changes it.'),
    }),
    z.object({
        ...optionBase,
        type: z.literal('color'),
        default: z
            .string()
            .describe('Hex colour used until the viewer changes it.'),
    }),
]));
const vizColorPalette = z
    .object({
    group: z
        .string()
        .nullable()
        .transform((value) => value ?? undefined)
        .optional()
        .describe('Optional tab name, matching a config option `group`. The picker gets its own tab when no option shares it.'),
})
    .nullable()
    .describe('Declare this when the component colours anything from `colorPalette`. It surfaces the standard Lightdash palette picker, so the viz inherits the same colours as the charts around it. Not a config option: the chosen colours arrive on `colorPalette`, never on `options`. Null when the component colours nothing.');
// Read validator for persisted schemas and app manifests. Older generated
// metadata may exceed today's authoring limits; keep those charts usable.
// `configOptions` defaults to `[]` for declarations from before it existed.
export const dataAppVizSchema = z.object({
    fields: vizFieldsForRead.refine(uniqueNames, 'duplicate field name'),
    configOptions: vizConfigOptions
        .default([])
        .refine(uniqueNames, 'duplicate option name'),
    colorPalette: vizColorPalette.default(null),
    inputGuidance: vizInputGuidance(false),
});
// The stricter contract handed to the generator CLI: `configOptions` and
// `colorPalette` are required, so an empty declaration is a deliberate answer
// rather than the shape of the schema's defaults.
export const dataAppVizGenerationSchema = z.object({
    fields: vizFieldsForGeneration.refine(uniqueNames, 'duplicate field name'),
    configOptions: vizConfigOptions
        .refine(uniqueNames, 'duplicate option name')
        .describe('Every setting the viewer can change from the chart config panel without regenerating the viz — one per literal the component would otherwise hardcode: what it shows or hides, which variant it picked, and the numbers and labels it wrote in. Each `name` must be a key the component reads from `options`. Series colours are not among them: declare `colorPalette` instead. Empty is only right for a component that hardcodes nothing a viewer would want different.'),
    colorPalette: vizColorPalette,
    inputGuidance: vizInputGuidance(true),
});
const dataAppVizSchemaMatchesApiType = true;
void dataAppVizSchemaMatchesApiType;
// Whatever the generator emits must be persistable without a second shape.
const dataAppVizGenerationSchemaIsPersistable = true;
void dataAppVizGenerationSchemaIsPersistable;
const isJsonSchemaObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const makeOpenAiStrict = (value) => {
    if (Array.isArray(value)) {
        return value.map(makeOpenAiStrict);
    }
    if (!isJsonSchemaObject(value)) {
        return value;
    }
    const schema = Object.fromEntries(Object.entries(value).map(([key, child]) => [
        key,
        makeOpenAiStrict(child),
    ]));
    if (!isJsonSchemaObject(schema.properties)) {
        return schema;
    }
    const required = new Set(Array.isArray(schema.required) ? schema.required : []);
    const properties = Object.fromEntries(Object.entries(schema.properties).map(([property, propertySchema]) => [
        property,
        required.has(property)
            ? propertySchema
            : { anyOf: [propertySchema, { type: 'null' }] },
    ]));
    return {
        ...schema,
        properties,
        required: Object.keys(properties),
    };
};
export const dataAppVizJsonSchema = makeOpenAiStrict(toLlmJsonSchema(dataAppVizGenerationSchema));
/** Whether a stored value still has the shape the option declares. */
const matchesDeclaredType = (option, value) => {
    switch (option.type) {
        case 'boolean':
            return typeof value === 'boolean';
        case 'number':
            return typeof value === 'number';
        case 'select':
            // A choice dropped by a regeneration is as stale as a wrong type.
            return option.choices.some((choice) => choice.value === value);
        case 'text':
        case 'color':
            return typeof value === 'string';
        default:
            return assertUnreachable(option, 'Unknown data app viz config option type');
    }
};
/**
 * Effective value of one option: the stored value, or the declared default when
 * nothing is stored or the stored value no longer matches the declared type —
 * stored values are untyped JSONB and a regeneration can change an option's
 * type (derive, never seed).
 */
export const getEffectiveOptionValue = (option, storedValue) => storedValue !== undefined && matchesDeclaredType(option, storedValue)
    ? storedValue
    : option.default;
/**
 * The explicitly stored values that still fit the declared options. Used when
 * a chart moves to a newer contract: stale names and mistyped values go,
 * defaults are still never seeded.
 */
export const pruneDataAppVizOptionValues = (configOptions, optionValues) => Object.fromEntries(configOptions.flatMap((option) => {
    const stored = optionValues[option.name];
    return stored !== undefined && matchesDeclaredType(option, stored)
        ? [[option.name, stored]]
        : [];
}));
/** Effective values for every declared option. */
export const getEffectiveOptionValues = (configOptions, optionValues) => Object.fromEntries(configOptions.map((o) => [
    o.name,
    getEffectiveOptionValue(o, optionValues[o.name]),
]));
/** Whether a chart type is a registry install: read-only, only editable by forking. */
export const isOfficialChartType = (viz) => viz.registrySlug !== null;
// postMessage type the host uses to push render context into the sandboxed
// iframe over the existing app SDK bridge (no new transport).
export const APP_SDK_DATA_APP_VIZ_CONTEXT_MESSAGE = 'lightdash:sdk:data-app-viz-context';
// postMessage type the iframe posts on mount (via the SDK's useVizContext) to
// ask the host to push the current context — the renderer may mount after the
// host's first push, so this handshake replaces blind timed re-sends.
export const APP_SDK_VIZ_CONTEXT_REQUEST_MESSAGE = 'lightdash:sdk:viz-context-request';
// postMessage type the host uses to tell a running app which color scheme to
// render in. Also seeded into the iframe URL hash as `theme=` so the SDK can
// apply the right scheme before the app's first render.
export const APP_SDK_COLOR_SCHEME_MESSAGE = 'lightdash:sdk:theme';
// Posted by the app's SDK once its theme listener is live, so the host replies
// with the current scheme instead of the app depending on having been listening
// when the load-time push went out.
export const APP_SDK_COLOR_SCHEME_REQUEST_MESSAGE = 'lightdash:sdk:theme-request';
// AI analysis pushed into a running app (mirrors packages/query-sdk/src/insights.ts).
// Host → iframe with the current analysis, in reply to a request and on change.
export const APP_SDK_INSIGHTS_MESSAGE = 'lightdash:sdk:insights';
// Iframe → host once the SDK's listener is live (and on sdk:ready);
// `inUse: true` once a hook subscribed, i.e. the app renders analysis.
export const APP_SDK_INSIGHTS_REQUEST_MESSAGE = 'lightdash:sdk:insights-request';
// Iframe → host: the viewer clicked an action rendered by the app.
export const APP_SDK_INSIGHT_ACTION_MESSAGE = 'lightdash:sdk:insight-action';
// Iframe → host: which query uuids are rendered on screen right now.
export const APP_SDK_MOUNTED_QUERIES_MESSAGE = 'lightdash:sdk:mounted-queries';
// Bridge-only virtual route: the viz posts semantic click intent here and the
// host rewrites it into the real underlying-data POST. Never forwarded to the
// API — `useAppSdkBridge` resolves it before allowlist matching.
export const APP_SDK_VIZ_UNDERLYING_DATA_PATH = '/__sdk/viz/underlying-data';
// Bridge-only virtual route for the current host-owned underlying-data dialog.
// The fetch path above remains available for deployed bundles that still draw
// their own dialog.
export const APP_SDK_VIZ_UNDERLYING_DATA_OPEN_PATH = '/__sdk/viz/underlying-data/open';
// Bridge-only virtual route: the viz posts a drill click intent here and the
// host resolves it and opens its drill dialog. Never forwarded to the API —
// `useAppSdkBridge` answers it before allowlist matching.
export const APP_SDK_VIZ_DRILL_DOWN_PATH = '/__sdk/viz/drill-down';
// Bridge-only virtual route: the viz posts a data-point click here and the
// host renders its native context menu over the iframe. Never forwarded to
// the API — `useAppSdkBridge` answers it before allowlist matching.
export const APP_SDK_VIZ_POINT_MENU_PATH = '/__sdk/viz/point-menu';
//# sourceMappingURL=types.js.map